<!-- Toolbar Edit action is registered declaratively in the component via
     PageHeaderService.setPageActions(...). Shown only when the current user
     has write permission on the recipe. -->

<div class="recipe-page-wrapper">

  <!-- Main scrollable content area -->
  <div class="recipe-main-content">
    <div class="mx-auto py-3 px-4">
      @if (errorMessage()) {
        <div class="alert alert-error" role="alert">
          {{ errorMessage() }}
        </div>
      }

      @if (isLoading()) {
        <div class="text-base-content/60">Loading recipe…</div>
      } @else {
        @if (recipe()) {
          <!-- Basic info + timeline -->
          <div class="mb-4 recipe-hero" [class.recipe-hero--has-image]="!!heroImageUrl()">
            <div class="flex flex-col md:flex-row items-stretch md:items-start gap-4">
              @if (heroImageUrl(); as heroUrl) {
                <div
                  class="recipe-hero__media group"
                  [class.recipe-hero__media--editable]="canManageImages()"
                  (click)="onImageClick()">
                  <img
                    [src]="heroUrl"
                    class="recipe-hero__image"
                    [alt]="recipe()!.title"/>
                  @if (canManageImages()) {
                    <!-- Desktop hover overlay (matches product detail) -->
                    <div class="recipe-hero__edit-overlay hidden md:flex absolute inset-0 items-center justify-center bg-base-100/60 opacity-0 group-hover:opacity-100 transition-opacity">
                      <fa-icon [icon]="faCamera" class="text-2xl text-primary"></fa-icon>
                    </div>
                  }
                </div>
              } @else if (canManageImages()) {
                <div class="recipe-hero__placeholder-wrap">
                  <button
                    type="button"
                    class="recipe-hero__placeholder"
                    (click)="onImageClick()">
                    <span class="recipe-hero__placeholder-icon">
                      <fa-icon [icon]="faImage"></fa-icon>
                    </span>
                    <span class="recipe-hero__placeholder-title">Add a photo</span>
                    <span class="recipe-hero__placeholder-subtitle">Give this recipe a title image</span>
                  </button>
                  <ui-button
                    [variant]="'secondary'"
                    [tone]="'outline'"
                    [size]="'sm'"
                    [fullWidth]="true"
                    (clicked)="onImageSearchClick()">
                    <fa-icon [icon]="faSearch" class="mr-1"></fa-icon>
                    Search image
                  </ui-button>
                </div>
              }

              <div class="recipe-hero__content grow min-w-0">
                <div class="flex flex-col md:flex-row items-start md:justify-start gap-3">

                  <div class="recipe-meta">
                    <div class="meta-item portion-meta">
                      <fa-icon [icon]="faUsers" class="me-2"></fa-icon>
                      <span class="label">Servings</span>
                      @if (canScalePortions()) {
                        <div class="portion-stepper" [class.is-rescaling]="isRescaling()">
                          <button
                            type="button"
                            class="portion-btn"
                            (click)="changePortions(-1)"
                            [disabled]="currentServings() <= 1"
                            aria-label="Fewer servings">−</button>
                          <span class="portion-count" aria-live="polite">{{ currentServings() }}</span>
                          <button
                            type="button"
                            class="portion-btn"
                            (click)="changePortions(1)"
                            [disabled]="currentServings() >= 99"
                            aria-label="More servings">+</button>
                        </div>
                        @if (isRescaling()) {
                          <span class="loading loading-spinner loading-xs portion-spin" role="status">
                            <span class="sr-only">Rescaling…</span>
                          </span>
                        }
                        @if (isPortionsScaled()) {
                          <button
                            type="button"
                            class="portion-reset"
                            (click)="resetPortions()"
                            [uiTooltip]="'Reset to ' + baseServings() + ' servings'"
                            tooltipPosition="top">
                            <fa-icon [icon]="faRotateLeft" class="me-1"></fa-icon>
                            <span>from {{ baseServings() }}</span>
                          </button>
                        }
                      } @else {
                        <span class="value">{{ recipe()!.servings ?? '—' }}</span>
                      }
                    </div>

                    <div class="meta-item">
                      <fa-icon [icon]="faStopwatch" class="me-2"></fa-icon>
                      <span class="label">Prep</span>
                      <span class="value">{{ recipe()!.prep_time_minutes ?? '—' }} min</span>
                    </div>

                    <div class="meta-item">
                      <fa-icon [icon]="faClock" class="me-2"></fa-icon>
                      <span class="label">On the table</span>
                      <span class="value">{{ recipe()!.total_time_minutes ?? '—' }} min</span>
                    </div>

                    @if (recipe()!.is_public) {
                      <div class="meta-item">
                        <fa-icon [icon]="faGlobe" class="me-2"></fa-icon>
                        <span class="label">Sharing</span>
                        <span class="value">Public</span>
                      </div>
                    }

                    <div class="meta-item">
                      <fa-icon [icon]="sourceIcon(recipe()!.source)" class="me-2"></fa-icon>
                      <span class="label">Source</span>
                      @if (recipe()!.source?.type === 'import' && recipe()!.source?.url) {
                        <a
                          [href]="recipe()!.source?.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="value link link-hover">
                          {{ sourceLabel(recipe()!.source) }}
                        </a>
                      } @else {
                        <span class="value" [title]="recipe()!.created_by?.user_email || ''">{{ sourceLabel(recipe()!.source) }}</span>
                      }
                    </div>
                  </div>
                </div>

                @if (recipe()!.facets; as facets) {
                  @if (facets.course || facets.seasons?.length || facets.dish_types?.length) {
                    <div class="mt-3 flex flex-wrap items-center gap-2">
                      <app-recipe-facet-badges
                        [facetsValue]="facets"
                        [dishTypeLabels]="dishTypeLabels()"/>
                      @if (facets.user_edited) {
                        <span class="text-xs text-base-content/50 italic" title="Facets were set manually">
                          manuell angepasst
                        </span>
                      }
                    </div>
                  }
                }

                <!-- Ratings: quiet inline aggregate + my rating + lazy comment view -->
                <div class="mt-3">
                  <app-recipe-ratings
                    [recipeId]="recipe()!.id"
                    [initialRating]="recipe()!.rating"
                    [initialMyRating]="recipe()!.my_rating"
                    (accessRevoked)="onRatingAccessRevoked()"/>
                </div>
              </div>
            </div>
          </div>

          <div class="recipe-timeline">
            <!-- DaisyUI steps (vertical, left rail) -->
            <ui-steps orientation="vertical" className="w-full">
              @for (step of recipe()!.steps; track step.order) {
                <li
                  uiStepItem
                  [uiStepItemColor]="stepTimelineColor(step)"
                >
                  @if (step.type === 'cook') {
                    <div class="min-w-0">
                      <div class="font-semibold leading-snug">{{ cookSummary(step) }}</div>
                      @if (step.text_html?.trim().length) {
                        <div class="text-sm text-base-content/70 leading-snug mt-0.5"
                             [innerHTML]="stepTextHtml(step)"></div>
                      }
                    </div>
                  } @else if (step.type === 'wait') {
                    <div class="min-w-0">
                      <div class="font-semibold leading-snug">{{ waitSummary(step) }}</div>
                      @if (step.text_html?.trim().length) {
                        <div class="text-sm text-base-content/70 leading-snug mt-0.5"
                             [innerHTML]="stepTextHtml(step)"></div>
                      }
                    </div>
                  } @else {
                    @if (step.text_html?.trim().length) {
                      <div class="min-w-0" [innerHTML]="stepTextHtml(step)"></div>
                    }
                  }
                </li>
              }
            </ui-steps>
          </div>

          @if (recipe()!.updated_at) {
            <div class="recipe-updated">
              Updated {{ recipe()!.updated_at | date: 'd. MMM y, HH:mm' }}
            </div>
          }

        } @else {
          @if (!errorMessage()) {
            <div class="alert border border-base-300" role="alert">
              Recipe not found.
            </div>
          }
        }
      }
    </div>
  </div>

  <!-- Fixed right sidebar panel (like Nextcloud) -->
  @if (recipe()) {
    <div class="recipe-sidebar-panel">
      <ui-card [shadow]="false" [bordered]="true" className="ingredients-card">
      <div class="ingredients-list">
        <div class="flex justify-between items-center mb-3">
          <h5 class="text-lg font-semibold mb-0">Ingredients</h5>
          @if (isLoadingStockStatus()) {
            <div class="loading loading-spinner loading-sm text-primary" role="status">
              <span class="sr-only">Loading stock status...</span>
            </div>
          }
        </div>

        @if (ingredientVMs().length > 0) {
          @if (cartPreviewItems().length > 0) {
            <ui-button
              class="mb-3 block"
              variant="primary"
              size="sm"
              [fullWidth]="true"
              (clicked)="openCartPreview()"
            >
              <fa-icon [icon]="faCartPlus" class="me-2"></fa-icon>
              Add to shopping cart
            </ui-button>
          }
          <ul class="divide-y divide-base-300">
            @for (ingredient of ingredientVMs(); track ingredient.key) {
              <li class="py-3 first:pt-0">
                <div
                  class="flex justify-between items-center ingredient-row cursor-pointer"
                  (click)="toggleIngredientCollapse(ingredient.key)"
                >
                  <div class="flex items-center gap-2 grow min-w-0">
                    @if (getIngredientStockStatus(ingredient.key); as status) {
                      <fa-icon
                        [icon]="getStockStatusIcon(status)!"
                        [class]="getStockStatusColor(status)"
                        class="stock-status-icon"
                        [uiTooltip]="getStockStatusTooltip(status)"
                        [tooltipPosition]="'top'"
                      ></fa-icon>
                    } @else if (isLoadingStockStatus()) {
                      <div class="stock-status-icon-placeholder"></div>
                    }
                    <span class="ingredient-name">{{ ingredient.name }}</span>
                  </div>

                  <div class="flex items-center gap-2 ml-2 shrink-0">
                    @if (ingredient.quantityReadable) {
                      <ui-badge [variant]="'primary'">{{ ingredient.quantityReadable }}</ui-badge>
                    }
                    <button
                      type="button"
                      class="ingredient-cart-btn"
                      [class.added]="isAddedToCart(ingredient.key)"
                      [disabled]="isAddedToCart(ingredient.key)"
                      [uiTooltip]="isAddedToCart(ingredient.key) ? 'Added to cart' : 'Add to shopping cart'"
                      tooltipPosition="top"
                      (click)="addIngredientToCart(ingredient, $event)"
                    >
                      <fa-icon [icon]="isAddedToCart(ingredient.key) ? faCircleCheck : faCartPlus"></fa-icon>
                    </button>
                  </div>
                </div>

                @if (getIngredientMatches(ingredient.key); as matchData) {
                  <div
                    class="ingredient-matches mt-3"
                    [class.hidden]="isIngredientCollapsed(ingredient.key)"
                  >
                    @if (matchData.matches && matchData.matches.length > 0) {
                      <div class="matches-grid">
                        @for (match of matchData.matches; track match.product_id) {
                          <ui-card class="match-card" [shadow]="false">
                            <div class="match-image-container">
                              @if (match.thumb_img_url && !isImageError(match.product_id)) {
                                <img
                                  [src]="match.thumb_img_url"
                                  [alt]="match.name"
                                  class="match-image"
                                  (error)="onImageError(match.product_id)"
                                >
                              } @else {
                                <div class="match-image-placeholder">
                                  <fa-icon [icon]="faBowlFood" class="text-base-content/20"></fa-icon>
                                </div>
                              }
                            </div>
                            <div class="match-details">
                              <div class="match-name">{{ match.name }}</div>
                              <div class="match-meta">
                                @if (match.stock_available) {
                                  <ui-badge
                                    [variant]="availabilityBadgeVariant(match)"
                                    size="sm"
                                  >
                                    {{ availabilityBadgeText(match) }}
                                  </ui-badge>
                                }

                              </div>

                              <div class="text-base-content/60 text-xs mt-1">
                                {{ matchTypeLabel(match.match_type) }}
                              </div>
                            </div>
                          </ui-card>
                        }
                      </div>
                    } @else {
                      <div class="text-base-content/60 text-sm">No matches found</div>
                    }
                  </div>
                }
              </li>
            }
          </ul>
        } @else {
          <div class="text-base-content/60">No ingredients available.</div>
        }
      </div>
      </ui-card>
    </div>

    <!-- Shopping-cart preview modal. Wide grid so many items stay readable,
         unlike the narrow sidebar. Opened by the "Add to shopping cart" button. -->
    <ui-modal #cartPreviewModal title="Add to shopping cart" size="lg">
      @if (cartPreviewItems().length > 0) {
        <p class="text-sm text-base-content/60 mb-4">
          Items you may not have in stock. Tap to remove any you don't need,
          then add the rest to your cart.
        </p>

        <div class="cart-preview-grid">
          @for (item of cartPreviewItems(); track item.key) {
            <button
              type="button"
              class="cart-preview-card"
              [class.deselected]="!isCartSelected(item.key)"
              (click)="toggleCartSelection(item.key)"
            >
              @if (getIngredientStockStatus(item.key) === 'uncertain') {
                <fa-icon
                  [icon]="faQuestionCircle"
                  class="cart-preview-card__flag"
                  uiTooltip="Uncertain match"
                  tooltipPosition="top"
                ></fa-icon>
              } @else if (getIngredientStockStatus(item.key) === 'available') {
                <fa-icon
                  [icon]="faCheckCircle"
                  class="cart-preview-card__flag"
                  uiTooltip="Already in stock"
                  tooltipPosition="top"
                ></fa-icon>
              }
              <fa-icon
                [icon]="isCartSelected(item.key) ? faCircleXmark : faCirclePlus"
                class="cart-preview-card__toggle"
              ></fa-icon>

              <span class="cart-preview-card__name">{{ item.name }}</span>
              @if (item.quantityReadable) {
                <span class="cart-preview-card__qty">{{ item.quantityReadable }}</span>
              }
            </button>
          }
        </div>
      } @else {
        <div class="flex flex-col items-center text-center gap-3 py-8">
          <fa-icon [icon]="faCheckCircle" class="text-success text-3xl"></fa-icon>
          <div class="text-base-content/60">
            Nothing to buy — every ingredient is in stock or already added.
          </div>
        </div>
      }

      <div modal-footer class="flex items-center gap-2 w-full">
        <ui-button variant="ghost" (clicked)="closeCartPreview()">Cancel</ui-button>
        @if (cartPreviewItems().length > 0) {
          <ui-button
            variant="primary"
            [disabled]="selectedCartCount() === 0"
            (clicked)="addSelectedToCart()"
          >
            <fa-icon [icon]="faCartPlus" class="me-2"></fa-icon>
            {{ selectedCartCount() > 0 ? 'Add ' + selectedCartCount() + ' to cart' : 'Nothing selected' }}
          </ui-button>
        }
      </div>
    </ui-modal>

          <!-- Image management modal, opened from the hero image -->
          @if (canManageImages()) {
            <app-recipe-image-dialog
              [recipeId]="recipe()!.id"
              [sourceUrl]="imageSourceUrl()"
              (modalClosed)="onImageDialogClosed()">
            </app-recipe-image-dialog>
          }
  }
</div>
